import { useQueryClient, useQuery, useMutation } from '@tanstack/vue-query'
import type { ApiErrorClass } from '~/types/apiError'
import type {
  Customer,
  CustomerActivities,
  CustomerBalance,
  CustomerInstagram,
  CustomerTiktok,
  UpdateCompanyCustomer,
  UpdateCustomer
} from '~/types/customer'

export const useStoreCustomer = defineStore('customer', () => {
  const { isAuthenticated } = storeToRefs(useStoreAuth())
  const { langCode } = storeToRefs(useStoreStore())

  const getCustomerBalance = () => {
    return useQuery({
      queryKey: ['fetchCustomerBalance'],
      queryFn: async () => {
        if (!isAuthenticated.value) return 0

        try {
          const balance: CustomerBalance = await useNuxtApp().$api('/api/customers/point-transactions/')
          return balance.current_balance || 0
        } catch (error) {
          return 0
        }
      },
      placeholderData: 0,
      staleTime: 0
    })
  }

  const getCustomerActivities = (limit: number) => {
    return useQuery({
      queryKey: ['fetchCustomerActivities'],
      queryFn: async () => {
        return (await useNuxtApp().$api(`/api/customers/point-transactions/`, {
          params: {
            limit
          }
        })) as CustomerActivities
      },
      staleTime: 0
    })
  }

  const getWeglot = () => {
    return useQuery({
      queryKey: ['getWeglot'],
      queryFn: async () => {
        return (await useNuxtApp().$api(`/api/customers/weglot/account/`)) as { api_key?: string }
      },
      enabled: () => useStoreAuth().getJwtToken() !== null
    })
  }

  const getCegid = () => {
    return useQuery({
      queryKey: ['getCegid'],
      queryFn: async () => {
        return (await useNuxtApp().$api(`/api/customers/cegid/account/`)) as { id?: string }
      },
      enabled: () => useStoreAuth().getJwtToken() !== null
    })
  }

  const hasToWaitInstagramAccountLinked = ref(false)

  const { tokenInfo } = storeToRefs(useStoreAuth())

  const customerIdFromToken = computed(() => {
    if (!tokenInfo.value) return null

    return tokenInfo.value.customer_id
  })

  const extractAccount = (customer: Customer | null | undefined, type: string) => {
    if (!customer || !customer.social_accounts.length) return null

    const account = customer.social_accounts.find((account) => account.type === type)

    if (!account) return null

    return account.details
  }

  const getInstagramAccount = (customer: Customer | null | undefined) => {
    return extractAccount(customer, 'instagram') as CustomerInstagram['details'] | null
  }

  const getTiktokAccount = (customer: Customer | null | undefined) => {
    return extractAccount(customer, 'tiktok') as CustomerTiktok['details'] | null
  }

  const fetchCustomer = () => {
    return useQuery({
      queryKey: ['fetchCustomer'],
      queryFn: async () => {
        if (!isAuthenticated.value) return null

        const customer: Customer = await useNuxtApp().$api(`/api/customers/${customerIdFromToken.value}/`)
        if (hasToWaitInstagramAccountLinked.value && getInstagramAccount(customer)?.is_refreshing_profile === true) {
          throw new Error()
        }
        if (customer) {
          // Update user language API side if changed (for emailing, etc.)
          let userLanguage = navigator.language
          if (langCode.value && langCode.value != '') {
            userLanguage = langCode.value
          }
          if (userLanguage && customer.language !== userLanguage) {
            updateCompanyCustomerLang(customer.id, userLanguage)
          }
        }

        return customer ?? null
      },
      enabled: () => isAuthenticated.value,
      // 20 attempts max and 2.5 seconds delay between each
      retry: (failureCount) => (hasToWaitInstagramAccountLinked.value ? failureCount < 19 : failureCount < 2),
      retryDelay: () => (hasToWaitInstagramAccountLinked.value ? 2500 : 1000)
    })
  }

  const setHasToWaitInstagramLinked = (value: boolean) => {
    hasToWaitInstagramAccountLinked.value = value
  }

  const queryClient = useQueryClient()

  const updateCustomer = () => {
    return useMutation({
      mutationFn: async (updateData: UpdateCustomer) => {
        await useNuxtApp().$api(`api/customers/${updateData.customerId}/`, {
          method: 'PATCH',
          body: {
            first_name: updateData.firstname,
            email: updateData.email,
            last_name: updateData.lastname,
            gender: updateData.gender,
            phone_number: updateData.phoneNumber,
            birthdate: updateData.birthdate,
            application_data: updateData.applicationData
          }
        })
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['fetchCustomer'] })
      }
    })
  }

  const updateCompanyCustomerLang = async (companyCustomerId: string, newLang: string | null) => {
    const lang = newLang ? newLang.split('-')[0] : null
    return await useNuxtApp().$api(`api/customers/${companyCustomerId}/`, {
      method: 'PATCH',
      body: {
        language: lang
      }
    })
  }

  const updateCompanyCustomer = () => {
    return useMutation({
      mutationFn: async (updateData: UpdateCompanyCustomer) => {
        await useNuxtApp().$api(`api/customers/${updateData.customerId}/`, {
          method: 'PATCH',
          body: {
            optin_transactional: updateData.optinTransactional,
            optin_marketing: updateData.optinMarketing
          }
        })
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['fetchCustomer'] })
      }
    })
  }

  const getCustomerMailSubscriptionsExternal = (customerId: string, token: string) => {
    return useQuery({
      queryKey: ['customerSubscriptions'],
      queryFn: async () => {
        const customers: Customer[] = await useNuxtApp().$api('/api/customers/unsubscribe-optin/', {
          method: 'GET',
          params: {
            customer_id: customerId,
            token
          }
        })
        return customers.length > 0 ? customers[0] : null
      },
      staleTime: 0,
      enabled: false
    })
  }

  const updateCustomerMailSubscriptionsExternal = () => {
    return useMutation({
      mutationFn: async ({ token, updateData }: { token: string; updateData: UpdateCompanyCustomer }) => {
        await useNuxtApp().$api('/api/customers/unsubscribe-optin/', {
          method: 'PATCH',
          params: {
            customer_id: updateData.customerId,
            token
          },
          body: {
            optin_transactional: updateData.optinTransactional,
            optin_marketing: updateData.optinMarketing
          }
        })
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['customerSubscriptions'] })
      },
      onError: (error: ApiErrorClass) => {
        return error
      }
    })
  }

  return {
    getCustomerBalance,
    getWeglot,
    getCegid,
    fetchCustomer,
    updateCustomer,
    updateCompanyCustomer,
    setHasToWaitInstagramLinked,
    getCustomerActivities,
    getInstagramAccount,
    getTiktokAccount,
    getCustomerMailSubscriptionsExternal,
    updateCustomerMailSubscriptionsExternal
  }
})
